import type { userRoleEnum, userStatusEnum } from '@masterapps/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, readApiError } from '@/hooks/use-applications';
import { apiClient } from '@/lib/api-client';

// Papel e situação DERIVADOS do enum do banco (`packages/shared` · schemas/enums),
// nunca reescritos como união literal: foi assim que a entrada de `inactive` no
// `user_status` deixou três cópias divergentes pelo projeto.
export type UserRole = (typeof userRoleEnum.enumValues)[number];
export type UserStatus = (typeof userStatusEnum.enumValues)[number];

/**
 * As duas situações que a tela pode GRAVAR. `Extract` do tipo derivado, e não
 * literais soltos: se um dos dois saísse do enum do banco, isto para de compilar
 * em vez de mandar um status inexistente para a API. `pending` e `rejected`
 * ficam fora porque são marcação manual no banco — a rota também os recusa.
 */
export type UserStatusPatch = Extract<UserStatus, 'approved' | 'inactive'>;

/** Campos do retrato que podem divergir do RH — os que decidem acesso. */
export type UserDivergentField = 'filial' | 'setor';

/** O que o RH diz HOJE sobre a matrícula (espelho `totvs_collaborators`). */
export interface UserRhMirror {
  nome: string;
  funcao: string | null;
  setorNome: string | null;
  codFilial: string | null;
  filial: string | null;
}

/**
 * Usuário como `GET /api/users` devolve — espelho do `UserAdminDTO` de
 * `apps/api/src/routes/users.ts` (o pacote da API só publica o `AppType` do RPC,
 * então o tipo é declarado aqui; a documentação OpenAPI tem um lock `Exact<>`
 * que quebra o typecheck se o DTO da rota mudar sem a documentação mudar).
 *
 * Três blocos: identidade, o RETRATO gravado no onboarding (`matricula`,
 * `funcao`, `setorNome`, `totvsFilialCode` — é ELE que o filtro de acesso usa) e
 * o que o RH diz hoje (`rh`), com as flags já DERIVADAS NO SERVIDOR. A tela não
 * recalcula nenhuma dessas regras: só pinta.
 */
export interface UserRow {
  id: string;
  email: string;
  nome: string | null;
  imageUrl: string | null;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  matricula: string | null;
  funcao: string | null;
  setorNome: string | null;
  totvsFilialCode: string | null;
  /** CODSITUACAO da última conferência. Só 'A' (ativo) e 'F' (férias) dão acesso. */
  rhSituacao: string | null;
  rhCheckedAt: string | null;
  deactivatedAt: string | null;
  /**
   * Códigos estáveis da conciliação (`demitido_rh`, `situacao_inativa`,
   * `sem_matricula`) ou texto livre, quando um admin desativou à mão.
   */
  deactivationReason: string | null;
  rh: UserRhMirror | null;
  semMatricula: boolean;
  semEspelhoRh: boolean;
  divergente: boolean;
  divergencias: UserDivergentField[];
}

export function useUsers() {
  return useQuery<UserRow[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await apiClient.api.users.$get();
      if (!res.ok) throw new Error('Falha ao carregar usuários');
      const json = (await res.json()) as { data: UserRow[] };
      return json.data;
    },
  });
}

/**
 * Corpo aceito pelo `PATCH /api/users/:id`. Ao menos um entre `role` e `status`;
 * `status: 'inactive'` exige `deactivationReason` (a API responde 400 sem ele —
 * desativação sem motivo registrado é o que a auditoria não pode ter).
 */
export interface UpdateUserInput {
  role?: UserRole;
  status?: UserStatusPatch;
  deactivationReason?: string;
}

export interface UpdateUserVars {
  /** UUID na tabela local `users` — não é o ID do Clerk. */
  id: string;
  input: UpdateUserInput;
}

/**
 * `PATCH /api/users/:id` (admin) — desativar/reativar à mão e trocar papel.
 *
 * O erro vem como `ApiError` (status + code + mensagem do envelope) porque os
 * dois 409 da rota são exatamente o que a tela precisa MOSTRAR: lockout do
 * próprio administrador e reativação de matrícula que o RH não libera. Um
 * `Error` genérico apagaria a explicação.
 */
/**
 * Convite de admin como `GET /api/users/admin-invites` devolve. Pendente =
 * `consumedAt === null`; consumido vira trilha de auditoria (quem autorizou ×
 * quando a conta nasceu) e não pode ser revogado.
 */
export interface AdminInviteRow {
  id: string;
  email: string;
  createdAt: string;
  consumedAt: string | null;
  criadoPorEmail: string | null;
}

export function useAdminInvites() {
  return useQuery<AdminInviteRow[]>({
    queryKey: ['admin-invites'],
    queryFn: async () => {
      const res = await apiClient.api.users['admin-invites'].$get();
      if (!res.ok) throw new Error('Falha ao carregar as pré-autorizações');
      const json = (await res.json()) as { data: AdminInviteRow[] };
      return json.data;
    },
  });
}

/**
 * `POST /api/users/admin-invites` — pré-autoriza um e-mail: quando a conta for
 * criada com ele, já nasce admin. Os 409 da rota são mensagem de tela (e-mail
 * já autorizado; pessoa que já tem conta → "Tornar admin"), então `ApiError`.
 */
export function useCreateAdminInvite() {
  const qc = useQueryClient();
  return useMutation<AdminInviteRow, ApiError, string>({
    mutationFn: async (email: string) => {
      const res = await apiClient.api.users['admin-invites'].$post({ json: { email } });
      if (!res.ok) throw await readApiError(res, 'Falha ao pré-autorizar o e-mail');
      const json = (await res.json()) as { data: AdminInviteRow };
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-invites'] });
    },
  });
}

/** `DELETE /api/users/admin-invites/:id` — revoga uma pré-autorização pendente. */
export function useRevokeAdminInvite() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, ApiError, string>({
    mutationFn: async (id: string) => {
      const res = await apiClient.api.users['admin-invites'][':id'].$delete({ param: { id } });
      if (!res.ok) throw await readApiError(res, 'Falha ao revogar a pré-autorização');
      const json = (await res.json()) as { data: { id: string } };
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-invites'] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<UserRow, ApiError, UpdateUserVars>({
    mutationFn: async ({ id, input }: UpdateUserVars) => {
      const res = await apiClient.api.users[':id'].$patch({ param: { id }, json: input });
      if (!res.ok) throw await readApiError(res, 'Falha ao atualizar o usuário');
      const json = (await res.json()) as { data: UserRow };
      return json.data;
    },
    // Invalida a lista inteira em vez de remendar a linha: o DTO carrega flags
    // derivadas no servidor (divergência, espelho do RH) e refazer a conta no
    // cliente seria reimplementar a regra que a rota acabou de aplicar.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
